import { Link } from "react-router-dom";
import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { PrintButton } from "@/Common/Components/Buttons/PrintButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Guid } from "@/Common/Models/Guid";
import { Comment } from "@/Common/Models/Patient/Comment";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { MedicalNote } from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { CheckboxLabel } from "@/DesignSystem";
import { CreateNoteInView } from "@/Documents/Components/DocumentView/CreateNoteInView";
import { NewMedicalNoteNoSpecialityWarningPopup } from "@/Patient/Components/MedicalNote/NewMedicalNoteNoSpecialityWarningPopup";
import { ConfirmDeleteMedicalItemPopup } from "@/Patient/Components/MedicalSummary/ConfirmDeleteMedicalItemPopup";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { ValidationNote } from "@/Patient/Models/MedicalSummary/ValidationNote";
import { MedicalItemViewMode, MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";

import styles from "./BaseMedicalItemsView.module.scss";

import CreateDocumentIcon from "@/Globals/_images/create-document.svg";
import AddfavoriteIcon from "@/Globals/_images/icons/AddFavorite.png";
import ArrowBackIcon from "@/Globals/_images/icons/arrow-back.svg";
import FavoriteIcon from "@/Globals/_images/icons/Favorite.png";

interface IListItemRenderFunction {
    (input: MedicalItem, group?: MedicalItem[]): JSX.Element
}

interface IItemDetailsRenderFunction {
    (input: MedicalItem): JSX.Element
}

interface IItemDetailsEditRenderFunction {
    (input: MedicalItem): JSX.Element
}

interface IMedicalItemHeaderRenderFunction {
    (input: MedicalItem): JSX.Element
}

interface IDetailsExtraHeaderRenderFunction {
    (input: IMedicalItemsModel): JSX.Element
}

interface IItemDetailsTitleRenderFunction {
    (input: MedicalItem): string
}

interface IGetCPPVisibility {
    (input: MedicalItem): boolean
}

interface IMedicalItemsViewProps {
    PatientContactId: Guid;
    Model: IMedicalItemsModel;
    PatientModel?: Patient;
    ListItemRenderFunction?: IListItemRenderFunction;
    MedicalItemRenderFunction?: IItemDetailsRenderFunction;
    MedicalItemEditRenderFunction?: IItemDetailsEditRenderFunction;
    DetailsExtraHeaderRenderFunction?: IDetailsExtraHeaderRenderFunction;
    MedicalItemHeaderRenderFunction?: IMedicalItemHeaderRenderFunction;
    GetDetailsTitleFunction?: IItemDetailsTitleRenderFunction;
    GetCPPVisibility?: IGetCPPVisibility;
    toSearchableStringFunction?: (item: MedicalItem[]) => MedicalItem[];
    DetailsTitle?: string;
    listItemSortingFunction?: (groups: MedicalItem[]) => MedicalItem[][];
    SaveFunction?: () => void;
    SavePlusFunction?: () => void;
    emrType?: MedicalItemTypeEnum;

    CheckIfSaveEnabledFunction?: () => boolean;
    CheckIfSaveAndSyncEnabledFunction?: () => boolean;
    IsForEditActiveMedication?: boolean;
    showNewEntryControl?: boolean;
    disableSaveIfNotLastItem?: boolean;

    SubTitleComponent?: React.ReactNode;
    AdditionalTitleComponent?: JSX.Element;
    CanItemBeDeleted?: (item: MedicalItem) => boolean;
    InvalidateComponent?: JSX.Element;
    IsModifyButtonDisabled?: boolean;
    IsDeleteButtonHidden?: (item: MedicalItem) => boolean;
    additionalFooterControls?: JSX.Element | JSX.Element[];
    additionalLeftControlButton?: JSX.Element;

    /**
     * If set, indicates that a print button should be added and this callback invoked on click.
     */
    onPrintButtonClick?: () => void;

    onShowInMedicalSummaryClick?: (checked: boolean) => void;

    printButtonDataCy?: string;

    childActionPopup?: JSX.Element;

    showFavorites?: boolean;
}

class BaseMedicalItemsView<P extends IMedicalItemsViewProps, S extends BaseMedicalItemsViewState> extends BaseReactComponent<P, S> {
    constructor(props: P) {
        super(props);
        this.toggleAddAsFavorite = this.toggleAddAsFavorite.bind(this);
    }

    private toggleAddAsFavorite(e: React.SyntheticEvent): void {
        e.stopPropagation();
        sessionStore.dispatch(LocalPatientOperations.MedicalItems.ToggleAsFavorite);
    }

    private ExpanderClick(id: string): void {
        this.toggleRowCollapse(id);
    }

    private onNewEntryClick(isEditing: boolean): void {
        if (!isEditing || patientStore.IsMedicalItemFromOutsideNote) {
            sessionStore.dispatch(patientStore.MedicalItemPreviousRoute, {
                editContext: {
                    selectedMedicalProblemSubType: patientStore?.SelectedMedicalProblemSubType,
                    isMedicalItemFromOutsideNote: true,
                    medicalItemPreviousRoute: patientStore.MedicalItemPreviousRoute,
                    isCreatingNewMedicalItem: patientStore.IsCreatingNewMedicalItem
                }
            });
        }
    }

    private isEditingAllowed(model: IMedicalItemsModel): boolean {
        const reachedFrom = model.get("NavigationOrigin");
        const isFromNoteOrSummary = reachedFrom === MedicalItemViewOrigin.Note || reachedFrom === MedicalItemViewOrigin.Summary;

        return isFromNoteOrSummary && !model.get("IsSourceEncounterSignedOff");
    }

    public isEditing(model: IMedicalItemsModel): boolean {
        return this.isEditingAllowed(model) && model.get("ViewMode") == MedicalItemViewMode.Edit;
    }

    public render(): JSX.Element {
        const model = this.props.Model;

        if (model == null)
            return <div />;

        const emrTemplateId = model.get("SelectedEMRTemplateId");
        const allMedicalItems = model.get("AllMedicalItemsList").toArray();
        const medicalItemOfEMRTemplate = allMedicalItems.filter(x => x.EMRTemplateId == emrTemplateId);

        const anyMedicalItemOfTheSelectedEmrTemplate = medicalItemOfEMRTemplate.firstOrDefault();

        const title = this.props.GetDetailsTitleFunction
            ? this.props.GetDetailsTitleFunction(anyMedicalItemOfTheSelectedEmrTemplate)
            : this.props.DetailsTitle ?? "";

        // --- logic to display & format invalidation notes or comments
        const showInvalid = anyMedicalItemOfTheSelectedEmrTemplate ? anyMedicalItemOfTheSelectedEmrTemplate.EMRTemplateIsInvalid : false;
        const markAsValidLabel = showInvalid ? cacheStore.GetString("MarkAsValidLabel") : cacheStore.GetString("MarkAsInvalidLabel");
        const allComments = model.get("EMRTemplateComments");
        const allValidationNotes = model.get("EMRTemplateValidationNotes");
        let comments: Comment[] = (!(allComments == null) && !(allComments[emrTemplateId] == null)) ? allComments[emrTemplateId] : [];
        let validationNotes: ValidationNote[] = (!(allValidationNotes == null) && !(allValidationNotes[emrTemplateId] == null)) ? allValidationNotes[emrTemplateId] : [];

        if (comments && comments.length > 0) comments = ArrayHelper.SortDynamic(comments, "CreateDate", true);
        if (validationNotes && validationNotes.length > 0) validationNotes = ArrayHelper.SortDynamic(validationNotes, "SavedDate", true);

        const displayCommentText: boolean = comments && comments.length > 0;
        const displayValidationNotes: boolean = validationNotes && validationNotes.length > 0;
        const displayButtons: boolean = !model.get("IsInCommentEdit") && !model.get("IsInValidationEdit");

        let validationNotesRender: JSX.Element = null;
        let commentsRender: JSX.Element = null;

        var invalidClinician = "";
        if (showInvalid) {
            var lastValidation = ArrayHelper.SortDynamic(validationNotes, "SavedDate", true)[0];
            if (lastValidation)
                invalidClinician = "(" + cacheStore.GetCliniciansPersonsByContactFacetId(lastValidation.EmployeeId).NameWithPrefix + " : " + moment(lastValidation.SavedDate).format("YYYY-MM-DD HH:mm") + ")";
        }

        if (displayValidationNotes) {
            const tmpnotes: JSX.Element[] = [];

            validationNotes.forEach((x, idx) => {
                let commenter = cacheStore.GetCliniciansPersonsByContactFacetId(x.EmployeeId);
                if (commenter) {
                    var validStyle = "";
                    if (!x.IsValidNote)
                        validStyle = "red-text";

                    var tmp = (
                        <div
                            key={"note_" + x.Id + "_" + idx}
                            className="notes-collection-item"
                            data-cy={`medicalItems-validationNote-${idx}`}
                        >
                            <div className={"memo-header " + validStyle}><strong>{commenter.NameWithPrefix}</strong> <span className={validStyle}>{x.SavedDate != null ? moment(x.SavedDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(x.SavedDate).format("HH:mm:ss") : ""}</span></div>
                            <div className={"note-container " + validStyle}>
                                {JSXHelper.BreakStringIntoParagraphs(x.Note)}
                            </div>
                        </div>
                    );
                    tmpnotes.push(tmp);
                }
            });

            if (tmpnotes.length > 0) {
                validationNotesRender = (
                    <div className="form-row">
                        <h4 className="thin-font" style={{ marginBottom: "15px", fontWeight: "bold" }}>
                            {cacheStore.GetString("InvalidationNoteLabel")}
                        </h4>
                        {tmpnotes}
                    </div>
                );
            }
        }

        if (displayCommentText) {
            var tmpComments: JSX.Element[] = [];

            comments.forEach((x: Comment, idx: number) => {
                var commenter = cacheStore.GetCliniciansPersonsByContactFacetId(x.EmployeeId);
                if (commenter) {
                    var tmp = (
                        <div
                            key={"comment" + "_" + x.Id + "_" + idx}
                            data-cy={`medicalItems-comment-${idx}`}
                            className="notes-collection-item"
                        >
                            <div className="memo-header">
                                <strong>{commenter.NameWithPrefix + " "}</strong>
                                <span>{x.CreateDate != null ? moment(x.CreateDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(x.CreateDate).format("HH:mm:ss") : ""}</span>
                            </div>
                            <div className="note-container">
                                {JSXHelper.BreakStringIntoParagraphs(x.Value)}
                            </div>
                        </div>
                    );
                    tmpComments.push(tmp);
                }
            });

            if (tmpComments.length > 0) {
                commentsRender = (
                    <div className="form-row">
                        <h4 className="thin-font" style={{ marginBottom: "15px", fontWeight: "bold" }}>{cacheStore.GetString("CommentsLabel")}</h4>
                        {tmpComments}
                    </div>
                );
            }
        }
        // ---

        // various state control variables
        const currentEncounter = model.get("Encounter") ?? PatientModelHelper.getLatestUnapprovedEncounter(patientStore.PatientMedicalSummaryModels[this.props.PatientContactId]);
        const hasAnyTemplates = model.get("EMRTemplateList") && model.get("EMRTemplateList").count() > 0;
        const shouldRenderUnderTitle = Boolean(this.props.DetailsExtraHeaderRenderFunction) && hasAnyTemplates;
        const reachedFrom = model.get("NavigationOrigin");
        const hasNavigatedHereFromSummary = reachedFrom == MedicalItemViewOrigin.Summary;
        const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItemOfEMRTemplate).firstOrDefault();
        const isLatestMedicalItemInMoreRecentNote = !patientStore.IsMedicalItemFromOutsideNote && currentEncounter && latestMedicalItem && latestMedicalItem.RelatedEncounterId != currentEncounter.Id && moment(latestMedicalItem.ConsultationDate).isAfter(currentEncounter.ConsultationDate);

        const isVisibleInCPP = latestMedicalItem && this.props.GetCPPVisibility != null
            ? this.props.GetCPPVisibility(latestMedicalItem)
            : anyMedicalItemOfTheSelectedEmrTemplate && anyMedicalItemOfTheSelectedEmrTemplate.EMRTemplateIsVisibleInCPP;

        const isEditingAllowed = this.isEditingAllowed(model);
        const isEditingEnabled = isEditingAllowed && !isLatestMedicalItemInMoreRecentNote;
        const isEditing = isEditingEnabled && model.get("ViewMode") == MedicalItemViewMode.Edit;

        const isLatestEncounter = currentEncounter && latestMedicalItem && currentEncounter.Id === latestMedicalItem.RelatedEncounterId;
        let isNewEntryEnabled = this.props.showNewEntryControl && isEditingEnabled && !isEditing && isLatestEncounter;
        let isModifyButtonEnabled = isEditingEnabled && !isEditing;

        if (!cacheStore?.ClinicSettings.EnableDirectPatientProfileEdits && hasNavigatedHereFromSummary) {
            isNewEntryEnabled = false;
            isModifyButtonEnabled = false;
        }
        const uncommittedMedicalItem = model.get("MedicalItemsList").filter(x => !x.IsCommitted).first();
        const isUncommittedNotTheLatest = (latestMedicalItem?.CreationDate && uncommittedMedicalItem?.CreationDate && moment(latestMedicalItem.CreationDate).isAfter(moment(uncommittedMedicalItem.CreationDate)));
        const summaryModel = patientStore.PatientMedicalSummaryModels[this.props.PatientContactId];

        const isSaveDisabled =
            !!model.get("IsSaving")
            || (this.props.disableSaveIfNotLastItem && isUncommittedNotTheLatest)
            || (this.props.CheckIfSaveEnabledFunction?.() === false);

        return (
            <div className="medical-summary-container" data-cy="medicalItems">
                {this.props.childActionPopup}
                {summaryModel.get("ShowNoteCreationSpecialityErrorWarning") &&
                    <NewMedicalNoteNoSpecialityWarningPopup SummaryModel={summaryModel} />
                }
                <div className="tb medical-summary">
                    {this.renderList(model, isEditing)}

                    <div className="tbc right-primary">
                        <div data-hvar="60">
                            <div className="form-row clearfix">
                                <div className="flex-container">
                                    <div>
                                        <h3 className="thin-font word-break-all" data-cy="medicalItems-title">
                                            {title}
                                            {showInvalid && <span className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel").toUpperCase()} {invalidClinician}</span>}
                                        </h3>
                                        {this.props.SubTitleComponent}
                                    </div>
                                    {this.props.AdditionalTitleComponent}
                                    {!shouldRenderUnderTitle && (
                                        this.renderShowMedicalSummaryCheckbox(anyMedicalItemOfTheSelectedEmrTemplate?.EMRTemplateId ?? "", isVisibleInCPP)
                                    )}
                                </div>
                                {shouldRenderUnderTitle && (
                                    <div className={styles.extraHeaderContainer}>
                                        {this.renderShowMedicalSummaryCheckbox(anyMedicalItemOfTheSelectedEmrTemplate?.EMRTemplateId ?? "", isVisibleInCPP)}
                                        {this.props.DetailsExtraHeaderRenderFunction?.(model)}
                                    </div>
                                )}
                            </div>

                            <div data-hvar="60" id="autoscroll_0" className="scrollable">
                                {this.RenderMedicalItemDetails(model)}

                                {anyMedicalItemOfTheSelectedEmrTemplate != null && anyMedicalItemOfTheSelectedEmrTemplate.EMRTemplateIsInvalid == false && model.get("IsInValidationEdit") && (
                                    <div className="form-row">
                                        <h4 className="thin-font" style={{ marginBottom: "15px", fontWeight: "bold" }}>
                                            {cacheStore.GetString("AddInvalidationNoteLabel")}
                                        </h4>
                                        {this.props.InvalidateComponent ?? (
                                            <CreateNoteInView
                                                addNoteOperation={LocalPatientOperations.MedicalItems.SaveValidationNote}
                                                hideControlOperation={LocalPatientOperations.MedicalItems.ToggleInvalid}
                                            />
                                        )}
                                    </div>
                                )}

                                {validationNotesRender}

                                {commentsRender}

                                {model.get("IsInCommentEdit") && (
                                    <div className="form-row">
                                        <h4 className="thin-font" style={{ marginBottom: "15px", fontWeight: "bold" }}>
                                            {cacheStore.GetString("AddCommentsLabel")}
                                        </h4>
                                        <CreateNoteInView
                                            addNoteOperation={LocalPatientOperations.MedicalItems.SaveAddComment}
                                            hideControlOperation={LocalPatientOperations.MedicalItems.ToggleAddComment}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="meta-footer clearfix" data-cy="medicalItems-footer">
                            {hasAnyTemplates && displayButtons && (
                                <div className="pull-left">
                                    {!isGuidNullOrUndefinedOrEmpty(emrTemplateId) && (
                                        <ButtonControl
                                            dataCy="medicalItems-addComment"
                                            label={cacheStore.GetString("AddCommentsLabel")}
                                            className="btn btn-primary pull-left push-right-5"
                                            onClick={() => {
                                                sessionStore.dispatch(LocalPatientOperations.MedicalItems.ToggleAddComment);
                                                this.scrollViewToBottom();
                                            }}
                                        />
                                    )}

                                    {/** we have dropped the "make an invalid medical item valid again" feature, once an item is invalidated, tough luck */}
                                    {anyMedicalItemOfTheSelectedEmrTemplate && !anyMedicalItemOfTheSelectedEmrTemplate.EMRTemplateIsInvalid && !isEditing && (
                                        <ButtonControl
                                            onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalItems.ToggleInvalid)}
                                            label={markAsValidLabel}
                                            className="btn btn-primary pull-left push-right-5"
                                            dataCy="medicalItems-markAsInvalid"
                                        />
                                    )}
                                </div>
                            )}
                            {isEditingAllowed && isLatestMedicalItemInMoreRecentNote && (
                                <div className="pull-right" style={{ paddingTop: "6px" }}>
                                    <span className="red-text">{cacheStore.GetString("HasMoreRecentContributionWarning")}</span>
                                </div>
                            )}
                            <div className="pull-right" style={{ display: "flex", alignItems: "center" }}>
                                {this.props.disableSaveIfNotLastItem && isEditing && isUncommittedNotTheLatest &&
                                    <span className="red-text pull-left push-right-5">{cacheStore.GetString("HasNewEntryAddedWarningLabel")}</span>
                                }

                                {this.props.additionalFooterControls}

                                {isEditing && (
                                    <ButtonControl
                                        dataCy="medicalItems-cancel"
                                        label={cacheStore.GetString("CancelLabel")}
                                        className="btn btn-default pull-left push-right-5"
                                        onClick={() => sessionStore.dispatch(
                                            LocalPatientOperations.MedicalItems.Cancel,
                                            { item: "backtonote" }
                                        )}
                                    />
                                )}

                                {isEditingEnabled && isEditing && (
                                    <ButtonControl
                                        dataCy="medicalItems-save"
                                        disabled={isSaveDisabled}
                                        onClick={this.props.SaveFunction}
                                        label={cacheStore.GetString("SaveLabel")}
                                        className="btn btn-primary pull-left push-right-5"
                                    />
                                )}

                                {isEditingEnabled && isEditing && (
                                    <ButtonControl
                                        dataCy="medicalItems-savePlus"
                                        disabled={isSaveDisabled}
                                        onClick={this.props.SavePlusFunction}
                                        label={cacheStore.GetString("SaveLabel") + "[+]"}
                                        className="btn btn-primary pull-left push-right-5"
                                    />
                                )}

                                {isModifyButtonEnabled && (
                                    <ButtonControl
                                        dataCy={isLatestEncounter ? "medicalItems-modify" : "medicalItems-newEntry"}
                                        onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalItems.Modify)}
                                        label={isLatestEncounter ? cacheStore.GetString("ModifyLabel") : cacheStore.GetString("NewEntryLabel")}
                                        className="btn btn-primary pull-left push-right-5"
                                        disabled={this.props.IsModifyButtonDisabled}
                                    />
                                )}

                                {isNewEntryEnabled && (
                                    <ButtonControl
                                        dataCy="medicalItems-newEntry"
                                        onClick={() => sessionStore.dispatch(LocalPatientOperations.MedicalItems.NewEntry)}
                                        label={cacheStore.GetString("NewEntryLabel")}
                                        className="btn btn-primary pull-left push-right-5"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {model.get("ShowDeleteMedicalItemConfirmation") && <ConfirmDeleteMedicalItemPopup />}
            </div>
        );
    }

    // WARNING: unused prop is needed so that MedicalItemsScreeningTestView component can use IMedicalItemsViewProps type in componentDidUpdate()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public componentDidUpdate(prevProps: P): void {
        $(window).trigger("resize");
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        $(window).trigger("resize");
    }

    protected renderList(model: IMedicalItemsModel, isEditing: boolean): JSX.Element {
        const hasAnyTemplates = model.get("EMRTemplateList")?.count() > 0;
        const hasNavigatedHereFrom = model.get("NavigationOrigin");
        const orderToNavigate = `/orders/${pharmacyOrdersStore.currentPharmacyOrderId ?? ""}`;

        const isInActiveMedicationsPage = patientStore.MedicalItemPreviousRoute == MedicalNote.Edit_NewActiveMedication;
        return (
            <div className="fxheight tbc full-border left-primary">
                <div className={styles.container}>
                    <div className={styles.control}>
                        <div className={styles.pharmanetContainer}>
                            {hasNavigatedHereFrom === MedicalItemViewOrigin.Note && (
                                <button className="btn btn-primary medical-item-go-back-link" data-cy="medicalItems-back" onClick={this.BackToConsultationClick.bind(this)}>
                                    <ArrowBackIcon className="push-right-5" />
                                    <span>{cacheStore.GetString("BackToConsultationLabel")}</span>
                                </button>
                            )}
                            {hasNavigatedHereFrom === MedicalItemViewOrigin.Summary && (
                                <button className="btn btn-primary medical-item-go-back-link" onClick={this.BackToSummaryClick.bind(this)}>
                                    <ArrowBackIcon className="push-right-5" />
                                    <span>{cacheStore.GetString("lblBackToSummary")}</span>
                                </button>
                            )}
                            {hasNavigatedHereFrom === MedicalItemViewOrigin.PatientLabResultTab && (
                                <div>
                                    <button className="btn btn-primary medical-item-go-back-link" onClick={this.BackToSummaryPatientResult.bind(this)}>
                                        <ArrowBackIcon className="push-right-5" />
                                        <span>{cacheStore.GetString("BackToResultLabel")}</span>
                                    </button>
                                </div>
                            )}
                            {hasNavigatedHereFrom === MedicalItemViewOrigin.ResultTab && (
                                <div>
                                    <Link className="btn btn-primary medical-item-go-back-link" to="/results">
                                        <ArrowBackIcon className="push-right-5" />
                                        <span>{cacheStore.GetString("BackToResultLabel")}</span>
                                    </Link>
                                </div>
                            )}
                            {hasNavigatedHereFrom === MedicalItemViewOrigin.PharmacyOrders && (
                                <Link className="btn btn-primary medical-item-go-back-link" to={orderToNavigate}>
                                    <ArrowBackIcon className="push-right-5" />
                                    <span>{cacheStore.GetString("PharmacyOrders_BackToOrdersLabel")}</span>
                                </Link>
                            )}
                            <div className={styles.pharmanetButton}>
                                {(isInActiveMedicationsPage)
                                    && this.props.additionalLeftControlButton
                                }
                            </div>
                        </div>
                    </div>
                    <div className={styles.newEntryButton}>
                        <div className="search-field">
                            <div className="gf">
                                <span className="glyphicon glyphicon-search" />
                            </div>
                            {hasAnyTemplates && (
                                <TextboxControl
                                    debounceMs={400}
                                    value={model.get("SearchTerm")}
                                    className="fullWidth"
                                    operation={LocalPatientOperations.MedicalItems.SearchTermChanged}
                                    placeholder={cacheStore.GetString("SearchLabel")}
                                    dataCy="baseMedicalItemsView-searchBar"
                                />
                            )}
                        </div>
                        {(this.props.onPrintButtonClick || !isInActiveMedicationsPage) && (
                            <ButtonGroup>
                                {this.props.onPrintButtonClick && (
                                    <PrintButton
                                        dataCy={this.props.printButtonDataCy}
                                        onClickCallback={() => this.props.onPrintButtonClick()}
                                    />
                                )}
                                {!isInActiveMedicationsPage && (
                                    patientStore.MedicalItemPreviousRoute && cacheStore.ClinicSettings.EnableDirectPatientProfileEdits && (
                                        <button
                                            className={styles.newEntry}
                                            onClick={() => this.onNewEntryClick(isEditing)}
                                        >
                                            <div className={styles.newEntryIcon}>
                                                <CreateDocumentIcon />
                                            </div>
                                        </button>
                                    )
                                )}
                            </ButtonGroup>
                        )}
                    </div>
                </div>
                {this.renderItemList(model)}
            </div>
        );
    }

    protected renderItemList(model: IMedicalItemsModel): JSX.Element {
        let self = this;
        let renders: JSX.Element[] = [];
        let itemsToRender = model.get("AllMedicalItemsList") ? model.get("AllMedicalItemsList").toArray() : [];

        // ensure we are not listing items that are in creation mode
        itemsToRender = itemsToRender.filter(x => !x.IsNewMedicalItem);

        if (!IsNullOrWhiteSpace(model.get("SearchTerm")) && self.props.toSearchableStringFunction) {
            itemsToRender = self.props.toSearchableStringFunction(itemsToRender);
        }

        let groupsToRender: MedicalItem[][] = this.sortItemListPerEmrTemplate(itemsToRender);

        renders = groupsToRender.map((group, idx) => {
            let itemToDisplay = PatientMedicalItemsHelper.getLatestSavedMedicalItemForEMRTemplate(group);

            return (
                <div
                    className="pointer"
                    key={"__a" + idx}
                    data-cy={`medicalItems-list-item-${idx}`}
                    onClick={() => this.SetSelectedEMRTemplate(itemToDisplay.EMRTemplateId)}
                >
                    {this.props.ListItemRenderFunction(itemToDisplay, group)}
                </div>
            );
        });

        return (
            <div className="note-collection scrollable">
                {renders}
            </div>
        );
    }

    protected sortItemListPerEmrTemplate(items: MedicalItem[]): MedicalItem[][] {
        const hasCustomSorting = !!this.props.listItemSortingFunction;

        if (hasCustomSorting) {
            return this.props.listItemSortingFunction(items);
        }

        // Sort by the date of the latest item of each group
        return items.groupBy((item: MedicalItem) => item.EMRTemplateId)
            .sortByPropertyDesc(group => MedicalItemModel.getMedicalItemEffectiveDate(
                PatientMedicalItemsHelper.getLatestSavedMedicalItemForEMRTemplate(group)
            ));
    }

    protected RenderMedicalItemDetails(model: IMedicalItemsModel): JSX.Element {
        const self = this;
        const renders: JSX.Element[] = [];
        const isEditing = this.isEditing(model);
        const currentEncounter = model.get("Encounter") ?? PatientModelHelper.getLatestUnapprovedEncounter(patientStore.PatientMedicalSummaryModels[this.props.PatientContactId]);

        // Is already grouped by emrTemplate in PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo()
        let medicalItems = model.get("MedicalItemsList") ? model.get("MedicalItemsList").toArray() : [];
        medicalItems = medicalItems.sortByProperties(
            propertyComparer(x => moment(x.CreationDate).toDate()),
            propertyComparerDesc(x => x.MedicalItemId)
        );

        if (model.get("MedicalItemsList") && model.get("MedicalItemsList").count() > 0 || this.props.IsForEditActiveMedication) {
            medicalItems.forEach((x, idx) => {
                if (self.props.MedicalItemRenderFunction != null && self.props.MedicalItemRenderFunction != undefined) {
                    const medicalItemCreationDate = MedicalItemModel.getMedicalItemCreationDate(x);
                    const displayDelete = !x.IsCommitted
                        && currentEncounter
                        && (currentEncounter.CreationDate > medicalItemCreationDate || x.RelatedEncounterId == currentEncounter.Id)
                        && !this.props.IsDeleteButtonHidden?.(x);

                    const isLastItem = idx === medicalItems.length - 1;

                    let innerTable = null;
                    if (this.props.MedicalItemRenderFunction && !x.IsCollapsed) {
                        if (isEditing && x.IsCommitted == false && (!this.props.disableSaveIfNotLastItem || isLastItem))
                            innerTable = this.props.MedicalItemEditRenderFunction(x);
                        else
                            innerTable = this.props.MedicalItemRenderFunction(x);
                    }

                    // When a medical item is a copy, it does not have a medicalitemId
                    const medicalItemId = (x.displayId != null) ? x.displayId : x.MedicalItemId;
                    const itemHeaderContent = self.props.MedicalItemHeaderRenderFunction
                        ? self.props.MedicalItemHeaderRenderFunction(x)
                        : this.getMedicalItemHeader(x);
                    const isFavorite = !!model.get("IsFavorite");
                    const showIcon = this.props.showFavorites && !x.IsCommitted && isEditing;

                    const itemHeader = (
                        <div>
                            <div id={"_Id_" + x.MedicalItemId} key={"_dd" + medicalItemId} className="flex-container medical-reaction-item pointer" onClick={this.ExpanderClick.bind(this, medicalItemId)}>
                                {itemHeaderContent}
                                {showIcon && (
                                    <div className={`${styles.imageContainer} pointer`} onClick={this.toggleAddAsFavorite} data-cy={isFavorite ? "medicalItems-favoriteToggled" : "medicalItems-favoriteUntoggled"}>
                                        <img className={styles.image} id={isFavorite ? "_setFavOn" : "_setFavOff"} src={isFavorite ? FavoriteIcon : AddfavoriteIcon} />
                                    </div>
                                )}
                                {displayDelete && !x.IsNewMedicalItem && (
                                    <div className="flx-col d" data-cy="medicalItems-delete" onClick={this.RemoveMedicalItem.bind(this, x)}>
                                        <span style={{ color: "red", fontWeight: "bold", cursor: "pointer" }}>X</span>
                                    </div>
                                )}
                            </div>
                            {innerTable}
                        </div>
                    );
                    renders.push(itemHeader);
                }
            });
        }

        return (
            <div>
                {renders.map((r, idx) => (
                    <div className="form-row" key={"header___" + idx}>{r}</div>
                ))}
            </div>
        );
    }

    protected renderShowMedicalSummaryCheckbox(EMRTemplateId: string, isVisibleInCPP: boolean): JSX.Element {
        return (
            <CheckboxLabel
                checked={isVisibleInCPP}
                label={cacheStore.GetString("ShowInMedicalSummaryLabel")}
                onChange={() => this.ToggleCPPVisibility(EMRTemplateId, isVisibleInCPP)}
                dataCy="medicalItems-toggleCPPVisibility"
            />
        );
    }

    protected toggleRowCollapse(id: string): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalItems.ToggleItemCollapse, {
            value: id
        });
    }

    protected SetSelectedEMRTemplate(emrTemplateId: string): void {
        const model = this.props.Model;
        const reachedFrom = model.get("NavigationOrigin");
        const hasNavigatedHereFromSummary = reachedFrom == MedicalItemViewOrigin.Summary;

        sessionStore.dispatch(LocalPatientOperations.MedicalItems.SelectEMRTemplate, {
            value: emrTemplateId,
            cancelEdit: true
        });

        if (patientStore.IsCreatingNewMedicalItem && patientStore.IsMedicalItemFromOutsideNote && !hasNavigatedHereFromSummary) {
            sessionStore.dispatch(LocalPatientOperations.MedicalItems.Modify);
        }
    }

    protected ToggleCPPVisibility(emrTemplateId: string, isVisibleInCPP: boolean): void {
        this.props.onShowInMedicalSummaryClick?.(!isVisibleInCPP);
        sessionStore.dispatch(LocalPatientOperations.MedicalItems.ToggleVisibility, {
            value: emrTemplateId,
            isDisplayedInCPP: this.props.GetCPPVisibility ? !isVisibleInCPP : null
        });
    }

    protected RemoveMedicalItem(medicalItem: MedicalItem, e: React.MouseEvent<HTMLDivElement>): void {
        e.stopPropagation();
        if (this.props.CanItemBeDeleted?.(medicalItem) === false)
            return;

        medicalItem.EMRTemplateMedicalItemType = this.props.emrType;
        sessionStore.dispatch(LocalPatientOperations.MedicalItems.RemoveMedicalItem, { medicalItem: medicalItem });

        patientStore.deleteMedicalItemCachedState(this.props.PatientContactId, this.getItemCacheId(this.props));
    }

    protected BackToSummaryClick(): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToSummary);
        LocalEventDispatcher.dispatch(operation);
    }

    protected BackToSummaryPatientResult(): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToPatientResults);
        LocalEventDispatcher.dispatch(operation);
    }

    protected BackToConsultationClick(): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToNote);
        LocalEventDispatcher.dispatch(operation);
    }

    protected backToDocumentsClick(): void {
        sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, { value: PatientTab.MedicalSummary });
        RouterHistory.push("documents");
    }

    protected backToPatientDocumentsClick(): void {
        sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, { value: PatientTab.Documents });
    }

    protected scrollViewToBottom(): void {
        window.setTimeout(() => {
            var el = $("#autoscroll_0");
            var h = el.get(0).scrollHeight;
            el.scrollTop(h);
        }, 300);
    }

    protected getItemCacheId(props: IMedicalItemsViewProps): string {
        if (props && props.Model && props.Model.get("ViewMode") == MedicalItemViewMode.Edit && props.Model.get("MedicalItemsList").count() > 0) {
            let result = props.Model.get("MedicalItemsList").find(obj => obj.IsCommitted === false);
            if (result != null)
                return result.MedicalItemId;
        }
        return "";
    }

    protected getMedicalItemHeader(medicalItem: MedicalItem): JSX.Element {
        let signingEmployeeString = "";
        let dateString = "";
        let employee = cacheStore.GetCliniciansPersonsByContactFacetId(medicalItem.EmployeeId);
        let createEmployeeString = (employee == null) ? "" : employee.NameWithPrefix;

        let signingEmployee = cacheStore.GetCliniciansPersonsByContactFacetId(medicalItem.SigningEmployeeEmployeeId);

        dateString = (medicalItem.CommitDate && medicalItem.IsCommitted) ? moment(medicalItem.CommitDate).format(`YYYY-MM-DD [${cacheStore.GetString("AtLabel")}] HH:mm:ss`) : "";
        signingEmployeeString = signingEmployee ? cacheStore.GetString("SignedOffByDLabel") + " " + signingEmployee.NameWithPrefix : "";

        return (
            <div className="flex-container" style={{ width: "100%" }}>
                <div className="flx-col a">
                    {createEmployeeString}
                </div>
                <div className="flx-col b">
                    {signingEmployeeString}
                </div>
                <div className="flx-col c">
                    {medicalItem.IsCommitted &&
                        <span>{dateString}</span>
                    }
                </div>
            </div>
        );
    }
}

export { BaseMedicalItemsView, IDetailsExtraHeaderRenderFunction, IItemDetailsEditRenderFunction, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps };
